import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter,
    ModalBody, ModalCloseButton, Button, FormControl, FormLabel,
    Input, VStack, HStack, useToast, Box, Text, Select,
    AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
    AlertDialogContent, AlertDialogOverlay, useDisclosure
} from '@chakra-ui/react';
import MultiSelectClass from './MultiSelectClass';
import { authAPI } from '../services/api';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getGradeOptions, getGradeLabelOverride } from '../utils/gradeLabels';

const StudentModal = ({ isOpen, onClose, isEditMode, studentId, onSuccess, classes }) => {
    const toast = useToast();
    // [A2] 年級下拉依登入者機構 grade_labels 渲染；沒設定（天籟現況）時 fallback 回現行 1-9「N 年級」
    const { user } = useAuth();
    const gradeLabels = user?.institution?.grade_labels || null;
    const gradeOptions = getGradeOptions(gradeLabels);
    const [loading, setLoading] = useState(false);
    const [archiveLoading, setArchiveLoading] = useState(false);
    const archiveDisclosure = useDisclosure(); // 移到舊生確認
    const cancelRef = useRef();
    const [schools, setSchools] = useState([]);
    const [formData, setFormData] = useState({
        full_name: '',
        grade: 1, // Default Grade 1
        class_ids: [],
        class_id: '', // Legacy
        school_id: '',
        parents: [
            { id: null, full_name: '', phone_number: '' }, // Parent 1
            { id: null, full_name: '', phone_number: '' }  // Parent 2
        ]
    });

    // Fetch schools when modal opens
    useEffect(() => {
        if (isOpen) {
            api.get('/schools')
                .then(res => setSchools(res.data || []))
                .catch(err => console.error('Failed to fetch schools', err));
        }
    }, [isOpen]);

    // Reset or Load Data
    useEffect(() => {
        if (isOpen) {
            if (isEditMode && studentId) {
                setLoading(true);
                authAPI.getStudent(studentId)
                    .then(res => {
                        const s = res.data;
                        const v2ids = s.classes ? s.classes.map(c => c.id) : [];
                        if (v2ids.length === 0 && s.class_id) v2ids.push(s.class_id);

                        // Load existing parents (up to 2)
                        const loadedParents = [
                            { id: null, full_name: '', phone_number: '' },
                            { id: null, full_name: '', phone_number: '' }
                        ];

                        if (s.parents && s.parents.length > 0) {
                            loadedParents[0] = {
                                id: s.parents[0].id,
                                full_name: s.parents[0].full_name,
                                phone_number: s.parents[0].phone_number
                            };
                            if (s.parents.length > 1) {
                                loadedParents[1] = {
                                    id: s.parents[1].id,
                                    full_name: s.parents[1].full_name,
                                    phone_number: s.parents[1].phone_number
                                };
                            }
                        }

                        setFormData({
                            full_name: s.full_name,
                            grade: s.grade || 1, // Backwards compatible default
                            class_ids: v2ids,
                            class_id: s.class_id || '',
                            school_id: s.school_id || '',
                            parents: loadedParents
                        });
                    })
                    .catch(err => {
                        console.error(err);
                        toast({ title: "載入失敗", status: "error" });
                        onClose();
                    })
                    .finally(() => setLoading(false));
            } else {
                // Reset for Create
                setFormData({
                    full_name: '',
                    grade: 1,
                    class_ids: [],
                    class_id: '',
                    school_id: '',
                    parents: [
                        { id: null, full_name: '', phone_number: '' },
                        { id: null, full_name: '', phone_number: '' }
                    ]
                });
            }
        }
    }, [isOpen, isEditMode, studentId, onClose, toast]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleParentChange = (index, field, value) => {
        const newParents = [...formData.parents];
        newParents[index] = { ...newParents[index], [field]: value };
        setFormData(prev => ({ ...prev, parents: newParents }));
    };

    const handleClassChange = useCallback((newClassIds) => {
        setFormData(prev => ({ ...prev, class_ids: newClassIds }));
    }, []);

    const handleSubmit = async () => {
        // Validation
        if (!formData.full_name || formData.class_ids.length === 0) {
            toast({ title: "資料不完整", description: "請填寫姓名並選擇至少一個班級", status: "warning", duration: 3000 });
            return;
        }

        // Parent 1 Phone is mandatory (Primary contact)
        if (!formData.parents[0].phone_number) {
            toast({ title: "缺少主要家長電話", description: "請至少填寫第一位家長的電話", status: "warning", duration: 3000 });
            return;
        }

        setLoading(true);
        try {
            // Prepare Student Payload
            const payload = {
                full_name: formData.full_name,
                grade: parseInt(formData.grade, 10),
                class_ids: formData.class_ids,
                status: 'NOT_ARRIVED', // Default status can be anything, backend ignores on update if null
                class_id: formData.class_ids.length > 0 ? formData.class_ids[0] : null,
                school_id: formData.school_id ? parseInt(formData.school_id) : null
            };

            // Filter valid parents (has phone number)
            const parentsToSubmit = formData.parents.filter(p => p.phone_number.trim() !== '');

            let finalStudentId = studentId;

            if (!isEditMode) {
                // CREATE MODE: Send parents list directly
                payload.parents = parentsToSubmit.map(p => ({
                    full_name: p.full_name || '家長',
                    phone_number: p.phone_number
                }));

                const res = await authAPI.createStudent(payload);
                finalStudentId = res.data.id;
                toast({ title: "新增成功", status: "success" });
            } else {
                // EDIT MODE: Update Student + Handle Parents individually
                // 1. Update Student Basic Info
                await authAPI.updateStudent(studentId, payload);

                // 2. Handle Parent Updates
                const parentPromises = formData.parents.map(async (p) => {
                    // Skip empty forms
                    if (!p.phone_number) return;

                    if (p.id) {
                        // Existing Parent -> Use Smart Update (Merge/Split)
                        try {
                            const smartRes = await authAPI.smartUpdateParent({
                                student_id: studentId,
                                old_parent_id: p.id,
                                new_phone: p.phone_number,
                                new_name: p.full_name
                            });

                            // Specific feedback for Merge/Split actions
                            if (smartRes.data.action !== 'NO_CHANGE' && smartRes.data.action !== 'UPDATED') {
                                toast({
                                    title: "家長帳號異動",
                                    description: smartRes.data.message,
                                    status: "info",
                                    duration: 5000,
                                    isClosable: true,
                                    position: "top"
                                });
                            }
                        } catch (err) {
                            console.error(`Parent update failed for ID ${p.id}`, err);
                            // Fallback error toast
                            const msg = err.response?.data?.detail || "家長資料更新失敗";
                            toast({ title: "家長更新錯誤", description: msg, status: "error" });
                        }
                    } else {
                        // New Parent -> Bind (Create if needed)
                        try {
                            await authAPI.bindParent(studentId, {
                                full_name: p.full_name || '家長',
                                phone_number: p.phone_number
                            });
                        } catch (err) {
                            console.error(`Bind parent failed`, err);
                        }
                    }
                });

                await Promise.all(parentPromises);
                toast({ title: "更新成功", status: "success" });
            }

            // Fetch fresh student data to return to parent
            // This is much lighter than re-fetching the whole list
            const freshStudentRes = await authAPI.getStudent(finalStudentId);
            const freshStudent = freshStudentRes.data;

            onSuccess(freshStudent);
            onClose();
        } catch (error) {
            console.error("Submit error:", error);
            const msg = error.response?.data?.detail || "操作失敗";
            toast({ title: "錯誤", description: msg, status: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleArchive = async () => {
        setArchiveLoading(true);
        try {
            await authAPI.archiveStudent(studentId);
            toast({
                title: "已移到舊生",
                description: `${formData.full_name || '該生'} 已移出班級，可在「舊生查詢」查看或復學`,
                status: "success", duration: 4000,
            });
            archiveDisclosure.onClose();
            if (onSuccess) onSuccess(null); // 觸發列表重新整理（離校生會從在籍列表消失）
            onClose();
        } catch (e) {
            toast({ title: "操作失敗", description: e.response?.data?.detail || '', status: "error" });
        } finally {
            setArchiveLoading(false);
        }
    };

    return (
        <>
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{isEditMode ? '編輯學生' : '新增學生'}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        {/* Student Info */}
                        <Box p={4} borderWidth="1px" borderRadius="lg" bg="gray.50">
                            <VStack spacing={4}>
                                <FormControl isRequired>
                                    <FormLabel>學生姓名</FormLabel>
                                    <Input
                                        name="full_name"
                                        value={formData.full_name}
                                        onChange={handleInputChange}
                                        placeholder="輸入學生姓名"
                                        bg="white"
                                    />
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel>年級</FormLabel>
                                    <Select
                                        name="grade"
                                        value={formData.grade}
                                        onChange={handleInputChange}
                                        bg="white"
                                    >
                                        {gradeOptions.map(g => (
                                            <option key={g} value={g}>{getGradeLabelOverride(g, gradeLabels) || `${g} 年級`}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>就讀學校</FormLabel>
                                    <Select
                                        name="school_id"
                                        value={formData.school_id || ''}
                                        onChange={handleInputChange}
                                        placeholder="選擇學校（選填）"
                                        bg="white"
                                    >
                                        {schools.map(s => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel>所屬班級 (可多選)</FormLabel>
                                    <MultiSelectClass
                                        options={classes}
                                        value={formData.class_ids}
                                        onChange={handleClassChange}
                                        placeholder="請選擇班級"
                                    />
                                </FormControl>
                            </VStack>
                        </Box>

                        <Text fontWeight="bold" fontSize="lg" mt={2}>家長資料 (最多兩位)</Text>

                        {/* Parent 1 */}
                        <Box p={4} borderWidth="1px" borderRadius="lg" borderColor="blue.200" bg="blue.50">
                            <Text mb={2} fontWeight="bold" color="blue.700">聯絡人1</Text>
                            <HStack spacing={2}>
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm">電話 (帳號)</FormLabel>
                                    <Input
                                        value={formData.parents[0].phone_number}
                                        onChange={(e) => handleParentChange(0, 'phone_number', e.target.value)}
                                        placeholder="09xx-xxx-xxx"
                                        bg="white"
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm">稱呼 (姓名)</FormLabel>
                                    <Input
                                        value={formData.parents[0].full_name}
                                        onChange={(e) => handleParentChange(0, 'full_name', e.target.value)}
                                        placeholder="王爸爸"
                                        bg="white"
                                    />
                                </FormControl>
                            </HStack>
                        </Box>

                        {/* Parent 2 */}
                        <Box p={4} borderWidth="1px" borderRadius="lg" borderColor="gray.200">
                            <Text mb={2} fontWeight="bold" color="gray.600">聯絡人2</Text>
                            <HStack spacing={2}>
                                <FormControl>
                                    <FormLabel fontSize="sm">電話 (帳號)</FormLabel>
                                    <Input
                                        value={formData.parents[1].phone_number}
                                        onChange={(e) => handleParentChange(1, 'phone_number', e.target.value)}
                                        placeholder="選填"
                                        bg="white"
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="sm">稱呼 (姓名)</FormLabel>
                                    <Input
                                        value={formData.parents[1].full_name}
                                        onChange={(e) => handleParentChange(1, 'full_name', e.target.value)}
                                        placeholder="選填"
                                        bg="white"
                                    />
                                </FormControl>
                            </HStack>
                        </Box>

                    </VStack>
                </ModalBody>
                <ModalFooter justifyContent="space-between">
                    {isEditMode ? (
                        <Button colorScheme="orange" variant="outline" onClick={archiveDisclosure.onOpen}>
                            移到舊生
                        </Button>
                    ) : <Box />}
                    <HStack>
                        <Button variant="ghost" onClick={onClose}>取消</Button>
                        <Button
                            colorScheme="brand"
                            onClick={handleSubmit}
                            isLoading={loading}
                        >
                            {isEditMode ? '儲存變更' : '新增學生'}
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>

        <AlertDialog
            isOpen={archiveDisclosure.isOpen}
            leastDestructiveRef={cancelRef}
            onClose={archiveDisclosure.onClose}
            isCentered
        >
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">移到舊生</AlertDialogHeader>
                    <AlertDialogBody>
                        確定將「{formData.full_name || '此學生'}」移到舊生？<br />
                        此學生將<b>從所有班級移除</b>，不再出現在日常名單、點名、接送與<b>家長端</b>；
                        資料會<b>保留</b>，日後可在「舊生查詢」<b>查詢或復學</b>。
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={archiveDisclosure.onClose}>取消</Button>
                        <Button colorScheme="orange" onClick={handleArchive} ml={3} isLoading={archiveLoading}>
                            確定移到舊生
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
        </>
    );
};

export default StudentModal;
